import { cn } from '@/lib/utils';

interface ISectionCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  // When true, the card fills its grid column height (lg+) and scrolls its body
  // instead of growing the column. Header stays pinned. Off below lg (stacked).
  fill?: boolean;
  // Override the default body spacing/padding when a section needs it.
  bodyClassName?: string;
  // Optional right-aligned header content (e.g. an "Add" button).
  action?: React.ReactNode;
}

/**
 * Section card with an icon-header band and a white body. Shared chrome for the
 * bot settings surfaces (Book Demo, Settings) so they stay visually consistent.
 */
const SectionCard: React.FC<ISectionCardProps> = ({
  icon: Icon, title, subtitle, children, fill, bodyClassName, action,
}) => (
  <section
    className={cn(
      'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
      fill && 'lg:flex lg:h-full lg:min-h-0 lg:flex-col',
    )}
  >
    <header
      className={cn(
        'flex items-start gap-3 border-b border-gray-100 bg-gray-50/70 px-6 py-4',
        fill && 'lg:shrink-0',
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </header>
    <div
      className={cn(
        bodyClassName || 'space-y-6 px-6 py-5',
        fill && 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col',
      )}
    >
      {children}
    </div>
  </section>
);

export default SectionCard;
